import React, { Component, Fragment } from "react";
import { connect } from "react-redux";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { UserApi } from "../../../api/user";
import { baseTheme } from "mui-theme";
import { ThemeProvider } from "@mui/material/styles";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  IconButton,
  Tooltip,
  Collapse,
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  SelectChangeEvent,
  TextField,
  InputAdornment,
} from "@mui/material";

import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import EditIcon from "@mui/icons-material/Edit";
import EditOffIcon from "@mui/icons-material/EditOff";
import DeleteIcon from "@mui/icons-material/Delete";
import LockResetIcon from "@mui/icons-material/LockReset";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import NotificationsIcon from "@mui/icons-material/Notifications";
import NotificationsOffIcon from "@mui/icons-material/NotificationsOff";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import AddIcon from "@mui/icons-material/Add";
import WarningIcon from "@mui/icons-material/Warning";
import SectionTitle from "../../elements/sectionTitle";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ACTIONS, hasPermission } from "App/common/permissionHandler";

interface Props {
  user: any;
  router: any;
  authData?: any;
  hotelData: {
    activeHotel: Record<string, any>;
    hotels: Array<{ hotelId: number; name: string }>;
  };
}

interface User {
  email: string;
  verificationStatus: boolean;
}

interface gnhStaff extends User {
  permissions: number;
}

interface hotelStaff extends User {
  permissions: Array<{
    hotelId: number;
    permission: number;
    notification: boolean;
  }>;
}

interface State {
  isConfirmDeleteDialogOpen: boolean;
  userToDelete: string | null;
  gnhUsers: Array<gnhStaff>;
  hotelUsers: Array<hotelStaff>;
  expandedUser: string | null;
  editingPermission: number | null;
  expandedHotelUser: string | null;
  editingHotelPermissions: { [hotelId: number]: number } | null;
  editingHotelNotifications: { [hotelId: number]: boolean } | null;
  proHotelAdminSearchQuery: string;
  hotelSearchQuery: string;
  selectedHotelFilter: number | "all";
  showAddGnhUserDialog: boolean; // For GNH users
  newGnhUserEmail: string;
  newGnhUserPermission: number | "";
  addGnhUserError: string | null;
  // For adding a new hotel to an existing user (during edit)
  newHotelIdToAdd: number | "";
  newHotelPermissionToAdd: number | ""; // Renamed for clarity from newHotelPermission

  // For adding a new Hotel User (dialog state)
  isAddHotelUserDialogOpen: boolean;
  newHotelUserEmail: string;
  newHotelUserPermissions: Array<{
    hotelId: number | "";
    permissionLevel: number | "";
    notificationsEnabled: boolean;
    hotelName?: string; // Optional: for display purposes in the dialog
  }>;
  currentHotelSelectionForNewUser: {
    hotelId: number | "";
    permissionLevel: number | "";
    notificationsEnabled: boolean;
  };
  addHotelUserError: string | null;
}

class UserDashboard extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      isConfirmDeleteDialogOpen: false,
      userToDelete: null,
      gnhUsers: [],
      hotelUsers: [],
      expandedUser: null,
      editingPermission: null,
      expandedHotelUser: null,
      editingHotelPermissions: null,
      editingHotelNotifications: null,
      proHotelAdminSearchQuery: "",
      hotelSearchQuery: "",
      selectedHotelFilter: "all",
      showAddGnhUserDialog: false,
      newGnhUserEmail: "",
      newGnhUserPermission: "",
      addGnhUserError: null,
      newHotelIdToAdd: "",
      newHotelPermissionToAdd: "", // Corrected state property name
      isAddHotelUserDialogOpen: false,
      newHotelUserEmail: "",
      newHotelUserPermissions: [],
      currentHotelSelectionForNewUser: {
        hotelId: "",
        permissionLevel: 30,
        notificationsEnabled: false,
      },
      addHotelUserError: null, // Initialize addHotelUserError
    };
  }

  async componentDidMount() {
    const { activeHotel } = this.props.hotelData;
    if (activeHotel && activeHotel.hotelId) {
      const res = await UserApi.getUsers(activeHotel.hotelId);
      this.setState({
        gnhUsers: [], // No longer used
        hotelUsers: res.users || [],
      });
    }
  }

  getPermissionText(permission: number): string {
    switch (permission) {
      case 30:
        return "Pro Hotel Admin";
      case 10:
        return "Pro Hotel Revenue Manager";
      default:
        return `Unknown (${permission})`;
    }
  }

  getHotelNameById = (hotelId: number): string => {
    const hotel = this.props.hotelData.hotels.find(
      (h) => h.hotelId === hotelId,
    );
    return hotel ? hotel.name : `ID: ${hotelId}`; // Fallback to ID if name not found
  };

  handleHotelPasswordReset = (user: hotelStaff) => {
    toast.promise(UserApi.resetUserPassword(user), {
      pending: "Resetting user password...",
      success: "User password reset successfully!",
      error: "Error resetting user password.",
    });
  };

  handleDeleteUser = async (email: string) => {
    this.handleOpenConfirmDeleteDialog(email);
  };

  handleOpenConfirmDeleteDialog = (email: string) => {
    this.setState({ isConfirmDeleteDialogOpen: true, userToDelete: email });
  };

  handleCloseConfirmDeleteDialog = () => {
    this.setState({ isConfirmDeleteDialogOpen: false, userToDelete: null });
  };

  handleConfirmDelete = () => {
    if (!this.state.userToDelete) return;

    const email = this.state.userToDelete;
    console.log(`Confirmed deletion for user: ${email}`);

    const apiCall = UserApi.deleteHotelUser(
      this.props.hotelData.activeHotel.hotelId,
      { email },
    );

    toast.promise(apiCall, {
      pending: `Deleting user ${email}...`,
      success: `User ${email} deleted successfully!`,
      error: {
        render({ data }: any) {
          let errorMessage = `Failed to delete user ${email}.`;
          if (data?.response?.data?.message) {
            errorMessage = data.response.data.message;
          } else if (data?.message) {
            errorMessage = data.message;
          }
          return errorMessage;
        },
      },
    });

    apiCall
      .then(async () => {
        // Refresh user list after successful deletion
        const res = await UserApi.getAllUsers();
        this.setState({
          gnhUsers: res.gnhStaff, // Assuming getAllUsers returns gnhStaff and hotelStaff
          hotelUsers: res.hotelStaff,
        });
      })
      .catch((error: any) => {
        // Error is already handled by toast.promise, but we can log it again if needed
        console.error(`Failed to delete user ${email}:`, error);
        // Optionally, set a more specific error state in the component if needed
      });

    this.handleCloseConfirmDeleteDialog();
  };

  handleProHotelAdminSearchChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    this.setState({ proHotelAdminSearchQuery: event.target.value });
  };

  handleHotelSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.setState({ hotelSearchQuery: event.target.value });
  };

  handleHotelFilterChange = (event: SelectChangeEvent<any>) => {
    this.setState({ selectedHotelFilter: event.target.value });
  };

  clearProHotelAdminSearch = () => {
    this.setState({ proHotelAdminSearchQuery: "" });
  };

  clearHotelSearch = () => {
    this.setState({ hotelSearchQuery: "" });
  };
  handleOpenAddGnhUserDialog = () => {
    this.setState({
      showAddGnhUserDialog: true,
      newGnhUserEmail: "",
      newGnhUserPermission: "",
      addGnhUserError: null,
    });
  };

  handleCloseAddGnhUserDialog = () => {
    this.setState({ showAddGnhUserDialog: false, addGnhUserError: null });
  };

  handleOpenAddHotelUserDialog = () => {
    this.setState({
      isAddHotelUserDialogOpen: true,
      newHotelUserEmail: "",
      newHotelUserPermissions: [],
      currentHotelSelectionForNewUser: {
        hotelId: "",
        permissionLevel: 30, // Default to Pro Admin
        notificationsEnabled: false,
      },
      addHotelUserError: null, // Clear previous errors
    });
  };

  handleCloseAddHotelUserDialog = () => {
    this.setState({ isAddHotelUserDialogOpen: false, addHotelUserError: null });
  };

  handleNewHotelUserInputChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const { name, value } = event.target;
    this.setState({ [name]: value } as any);
  };

  handleCurrentHotelSelectionChange = (event: SelectChangeEvent<any>) => {
    const { name, value } = event.target;
    this.setState((prevState) => ({
      currentHotelSelectionForNewUser: {
        ...prevState.currentHotelSelectionForNewUser,
        [name]: value,
      },
    }));
  };

  handleToggleCurrentHotelNotification = () => {
    this.setState((prevState) => ({
      currentHotelSelectionForNewUser: {
        ...prevState.currentHotelSelectionForNewUser,
        notificationsEnabled:
          !prevState.currentHotelSelectionForNewUser.notificationsEnabled,
      },
    }));
  };

  handleAddHotelPermissionToDialogList = () => {
    const { currentHotelSelectionForNewUser, newHotelUserPermissions } =
      this.state;
    const { hotelData } = this.props;

    if (
      !currentHotelSelectionForNewUser.hotelId ||
      !currentHotelSelectionForNewUser.permissionLevel
    ) {
      this.setState({
        addHotelUserError: "Please select a hotel and permission level.",
      });
      return;
    }

    const hotel = hotelData.hotels.find(
      (h) => h.hotelId === currentHotelSelectionForNewUser.hotelId,
    );

    const newPermissionEntry = {
      ...currentHotelSelectionForNewUser,
      hotelName: hotel ? hotel.name : "Unknown Hotel",
    };

    this.setState((prevState) => ({
      newHotelUserPermissions: [
        ...prevState.newHotelUserPermissions,
        newPermissionEntry,
      ],
      currentHotelSelectionForNewUser: {
        // Reset for next entry
        hotelId: "",
        permissionLevel: 30, // Or your preferred default
        notificationsEnabled: false,
      },
      addHotelUserError: null,
    }));
  };

  handleSaveNewHotelUser = async () => {
    const { newHotelUserEmail } = this.state;
    const { hotelData } = this.props;
    const activeHotelId = hotelData.activeHotel?.hotelId;

    if (!newHotelUserEmail.trim()) {
      this.setState({ addHotelUserError: "Email address is required." });
      return;
    }

    if (!/\S+@\S+\.\S+/.test(newHotelUserEmail)) {
      this.setState({
        addHotelUserError: "Please enter a valid email address.",
      });
      return;
    }

    if (!activeHotelId) {
      this.setState({
        addHotelUserError: "No active hotel selected.",
      });
      return;
    }

    try {
      this.setState({ addHotelUserError: null });
      const apiCall = UserApi.createHotelUser(activeHotelId, {
        email: newHotelUserEmail,
        permission: 10,
      });

      toast.promise(apiCall, {
        pending: "Creating hotel user...",
        success: "Hotel user created successfully!",
        error: {
          render({ data }: any) {
            let errorMessage = "Failed to create hotel user. ";
            if (data?.response?.data?.message) {
              errorMessage = data.response.data.message;
            } else if (data?.message) {
              errorMessage = data.message;
            }
            return errorMessage;
          },
        },
      });

      try {
        await apiCall;
        // Refresh user list after successful creation
        const updatedUsers = await UserApi.getUsers(activeHotelId);
        this.setState({
          hotelUsers: updatedUsers.users || [],
        });
        this.handleCloseAddHotelUserDialog();
      } catch (error: any) {
        console.error("Error creating hotel user:", error);
        let dialogErrorMessage = "Failed to create hotel user.";
        if (error?.response?.data?.message) {
          dialogErrorMessage = error.response.data.message;
        } else if (error?.message) {
          dialogErrorMessage = error.message;
        }
        this.setState({
          addHotelUserError: dialogErrorMessage,
        });
      }
    } catch (error: any) {
      console.error("Synchronous error in handleSaveNewHotelUser:", error);
      this.setState({
        addHotelUserError:
          error.message || "An unexpected synchronous error occurred.",
      });
    }
  };

  handleRemoveHotelPermissionFromDialogList = (indexToRemove: number) => {
    this.setState((prevState) => ({
      newHotelUserPermissions: prevState.newHotelUserPermissions.filter(
        (_, index) => index !== indexToRemove,
      ),
    }));
  };

  handleNewGnhUserInputChange = (
    event:
      | React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
      | SelectChangeEvent<number | "">,
  ) => {
    const target = event.target as
      HTMLInputElement | { name: string; value: unknown }; // Type assertion for SelectChangeEvent
    const name = target.name;
    let value = target.value;

    if (name === "newGnhUserPermission" && value !== "") {
      value = Number(value);
    }

    this.setState(
      (prevState) =>
        ({
          ...prevState,
          [name]: value,
        }) as Pick<State, keyof State>,
    );
  };

  handleSaveNewGnhUser = async () => {
    const { newGnhUserEmail } = this.state;

    if (!newGnhUserEmail) {
      const errorMsg = "Email is required.";
      this.setState({ addGnhUserError: errorMsg });
      toast.error(errorMsg);
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newGnhUserEmail)) {
      const errorMsg = "Please enter a valid email address.";
      this.setState({ addGnhUserError: errorMsg });
      toast.error(errorMsg);
      return;
    }

    this.setState({ addGnhUserError: null });

    const apiCall = UserApi.createHotelUser(
      this.props.hotelData.activeHotel.hotelId,
      {
        email: newGnhUserEmail,
        permission: 30,
      },
    );

    toast.promise(apiCall, {
      pending: "Creating new pro admin user...",
      success: "Pro admin user created successfully!",
      error: {
        render({ data }: any) {
          let errorMessage =
            "Failed to create pro admin user. Please try again.";
          if (data?.response?.data?.message) {
            errorMessage = data.response.data.message;
          } else if (data?.message) {
            errorMessage = data.message;
          }
          return errorMessage;
        },
      },
    });

    try {
      await apiCall;
      const res = await UserApi.getUsers(
        this.props.hotelData.activeHotel.hotelId,
      );
      this.setState({
        hotelUsers: res.users || [],
      });
      this.handleCloseAddGnhUserDialog();
    } catch (error: any) {
      console.error("Failed to create pro admin user:", error);
      let errorMessage = "Failed to create pro admin user. Please try again.";
      if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }
      this.setState({ addGnhUserError: errorMessage });
    }
  };

  renderAddAdminDialog = () => {
    const { showAddGnhUserDialog, newGnhUserEmail, addGnhUserError } =
      this.state;

    return (
      <Dialog
        open={showAddGnhUserDialog}
        onClose={this.handleCloseAddGnhUserDialog}
      >
        <DialogTitle>Add New Pro Admin User</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Enter the email address for the new pro admin user.
          </DialogContentText>
          {addGnhUserError && (
            <Typography color="error" sx={{ mb: 2 }}>
              {addGnhUserError}
            </Typography>
          )}
          <TextField
            autoFocus
            margin="dense"
            name="newGnhUserEmail"
            label="Email Address"
            type="email"
            fullWidth
            variant="standard"
            value={newGnhUserEmail}
            onChange={this.handleNewGnhUserInputChange}
            sx={{ mb: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={this.handleCloseAddGnhUserDialog}>Cancel</Button>
          <Button onClick={this.handleSaveNewGnhUser} variant="contained">
            Save User
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  renderAddHotelUserDialog = () => {
    const { isAddHotelUserDialogOpen, newHotelUserEmail, addHotelUserError } =
      this.state;

    return (
      <Dialog
        open={isAddHotelUserDialogOpen}
        onClose={this.handleCloseAddHotelUserDialog}
      >
        <DialogTitle>Add New Hotel User</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 1 }}>
            Enter the user&apos;s email address to add them to this hotel.
          </DialogContentText>

          {addHotelUserError && (
            <Typography color="error" sx={{ mb: 2 }}>
              {addHotelUserError}
            </Typography>
          )}

          <TextField
            autoFocus
            margin="dense"
            name="newHotelUserEmail"
            label="Email Address"
            type="email"
            fullWidth
            variant="standard"
            value={newHotelUserEmail}
            onChange={this.handleNewHotelUserInputChange}
            sx={{ mb: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={this.handleCloseAddHotelUserDialog}>Cancel</Button>
          <Button onClick={this.handleSaveNewHotelUser} variant="contained">
            Save User
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  // Filter GNH users based on search query
  getFilteredProHotelAdmins = () => {
    const { hotelUsers } = this.state;
    const { proHotelAdminSearchQuery } = this.state;
    const activeHotelId = this.props.hotelData.activeHotel?.hotelId;

    if (!activeHotelId) return [];

    return hotelUsers.filter((user) => {
      const isProAdminForHotel =
        Array.isArray(user.permissions) &&
        user.permissions.some(
          (p) => p.hotelId === activeHotelId && p.permission === 30,
        );

      if (!isProAdminForHotel) return false;

      const matchesSearch = user.email
        .toLowerCase()
        .includes(proHotelAdminSearchQuery.toLowerCase());

      return matchesSearch;
    });
  };

  // Filter Hotel users based on selected hotel and email search
  getFilteredHotelUsers = () => {
    const { hotelUsers, hotelSearchQuery } = this.state;
    const activeHotelId = this.props.hotelData.activeHotel?.hotelId;

    if (!activeHotelId || !hotelUsers) return [];

    let filteredUsers = hotelUsers.filter((user) => {
      return user.permissions.some(
        (p) => p.hotelId === activeHotelId && p.permission === 10,
      );
    });

    if (hotelSearchQuery) {
      const lowercasedQuery = hotelSearchQuery.toLowerCase();
      filteredUsers = filteredUsers.filter((user) =>
        user.email.toLowerCase().includes(lowercasedQuery),
      );
    }

    return filteredUsers;
  };

  // Render the GNH staff table
  renderProHotelAdminTable() {
    const { proHotelAdminSearchQuery } = this.state;
    const filteredProHotelAdmins = this.getFilteredProHotelAdmins();
    const currentUserEmail = this.props.user?.email;
    const isSystemAdmin = hasPermission(ACTIONS.MANAGE_PRO_HOTEL);

    return (
      <TableContainer
        component={Paper}
        sx={{ marginBottom: 4, width: "100%", margin: "0 auto" }}
      >
        <Box sx={{ p: 2, display: "flex", alignItems: "center", gap: 2 }}>
          <TextField
            variant="outlined"
            placeholder="Search by email"
            value={proHotelAdminSearchQuery}
            onChange={this.handleProHotelAdminSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: proHotelAdminSearchQuery ? (
                <InputAdornment position="end">
                  <IconButton
                    onClick={this.clearProHotelAdminSearch}
                    edge="end"
                    size="small"
                  >
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
            sx={{ flexGrow: 1 }}
          />

          {isSystemAdmin && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={this.handleOpenAddGnhUserDialog}
              sx={{ whiteSpace: "nowrap", minWidth: "120px" }}
            >
              New Pro Admin
            </Button>
          )}
        </Box>
        <Table
          sx={{ minWidth: 650, width: "100%" }}
          aria-label="Pro Hotel Admin table"
        >
          <TableHead>
            <TableRow>
              <TableCell>Email</TableCell>
              <TableCell align="right" style={{ paddingRight: "40px" }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredProHotelAdmins.length > 0 ? (
              filteredProHotelAdmins.map((staffUser) => (
                <React.Fragment key={staffUser.email}>
                  <TableRow>
                    <TableCell component="th" scope="row">
                      {staffUser.email}
                    </TableCell>
                    <TableCell align="right">
                      {!staffUser.verificationStatus && (
                        <>
                          <Tooltip title="User had not completed first log in">
                            <IconButton size="small" color="warning">
                              <WarningIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Reset Password">
                            <IconButton
                              onClick={() =>
                                this.handleHotelPasswordReset(staffUser)
                              }
                              size="small"
                              color="primary"
                            >
                              <LockResetIcon />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}

                      {isSystemAdmin &&
                        staffUser.email !== currentUserEmail && (
                          <>
                            <Tooltip title="Delete User">
                              <IconButton
                                onClick={() =>
                                  this.handleDeleteUser(staffUser.email)
                                }
                                size="small"
                                color="error"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  No users found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  renderConfirmDeleteDialog = () => {
    const { isConfirmDeleteDialogOpen, userToDelete } = this.state;

    return (
      <Dialog
        open={isConfirmDeleteDialogOpen}
        onClose={this.handleCloseConfirmDeleteDialog}
        aria-labelledby="confirm-delete-dialog-title"
      >
        <DialogTitle id="confirm-delete-dialog-title">
          Confirm Deletion
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to permanently remove the user from the hotel?
            <strong> {userToDelete}</strong>? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={this.handleCloseConfirmDeleteDialog}>Cancel</Button>
          <Button
            onClick={this.handleConfirmDelete}
            color="error"
            variant="contained"
            autoFocus
          >
            Confirm Delete
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  renderHotelStaffTable = () => {
    const { hotelSearchQuery } = this.state;
    const filteredHotelUsers = this.getFilteredHotelUsers();
    const currentUserEmail = this.props.user?.email;
    const isSystemAdmin = hasPermission(ACTIONS.MANAGE_PRO_HOTEL);

    return (
      <TableContainer
        component={Paper}
        sx={{ marginBottom: 4, width: "100%", margin: "0 auto" }}
      >
        <Box sx={{ p: 2, display: "flex", alignItems: "center", gap: 2 }}>
          <TextField
            variant="outlined"
            placeholder="Search by email"
            value={hotelSearchQuery}
            onChange={this.handleHotelSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: hotelSearchQuery ? (
                <InputAdornment position="end">
                  <IconButton
                    onClick={this.clearHotelSearch}
                    edge="end"
                    size="small"
                  >
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
            sx={{ flexGrow: 1 }}
          />

          {isSystemAdmin && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={this.handleOpenAddHotelUserDialog} // Correct handler for hotel user
              sx={{ whiteSpace: "nowrap", minWidth: "120px", ml: 1 }}
            >
              New User
            </Button>
          )}
        </Box>
        <Table sx={{ minWidth: 650 }} aria-label="Hotel staff table">
          <TableHead>
            <TableRow>
              <TableCell>Email</TableCell>
              <TableCell align="right" style={{ paddingRight: "40px" }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredHotelUsers.length > 0 ? (
              filteredHotelUsers.map((hotelUser) => (
                <React.Fragment key={hotelUser.email}>
                  <TableRow>
                    <TableCell component="th" scope="row">
                      {hotelUser.email}
                    </TableCell>
                    <TableCell align="right">
                      {!hotelUser.verificationStatus && (
                        <>
                          <Tooltip title="User had not completed first log in">
                            <IconButton size="small" color="warning">
                              <WarningIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Reset Password">
                            <IconButton
                              onClick={() =>
                                this.handleHotelPasswordReset(hotelUser)
                              }
                              size="small"
                              color="primary"
                            >
                              <LockResetIcon />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}

                      {isSystemAdmin &&
                        hotelUser.email !== currentUserEmail && (
                          <>
                            <Tooltip title="Delete User">
                              <IconButton
                                onClick={() =>
                                  this.handleDeleteUser(hotelUser.email)
                                }
                                size="small"
                                color="error"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={3} align="center">
                  No users found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  render() {
    return (
      <ThemeProvider theme={baseTheme}>
        <div className="content">
          <Box sx={{ width: "100%", margin: "0 auto" }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
              }}
            >
              <SectionTitle {...{ value: "Pro Hotel Admins" }} />
            </Box>
            {this.renderProHotelAdminTable()}
          </Box>
        </div>
        <div className="content" style={{ paddingBottom: "50px" }}>
          <Box sx={{ width: "100%", margin: "0 auto" }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
              }}
            >
              <SectionTitle {...{ value: "Hotel Staff" }} />
            </Box>
            {this.renderHotelStaffTable()}
          </Box>
        </div>
        {this.renderAddAdminDialog()}
        {this.renderAddHotelUserDialog()}
        {this.renderConfirmDeleteDialog()}
      </ThemeProvider>
    );
  }
}

const mapStateToProps = (state: any) => {
  return {
    user: state.user,
    hotelData: state.hotelData, // Add hotelData from Redux state
  };
};

// export default connect(mapStateToProps)(UserDashboard); // Keep this commented or remove if UserDashboardWithRouter is the sole export

const UserDashboardWithRouter = (props: Omit<Props, "router">) => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const router = { navigate, location, params };
  return <UserDashboard {...props} router={router} />;
};

export default connect(mapStateToProps)(UserDashboardWithRouter);
